import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, AfterViewInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';

import { AuthService } from '../../../services/auth.service';
import { ThemeService } from '../../../services/theme.service';
import { OpenModalConfirmService } from '../../../services/open-modal-confirm.service';
import { CountdownComponent } from "../../../components/double-authentication-countdown/double-authentication-countdown.component";

@Component({
  selector: 'app-safe-trace-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatSnackBarModule,
    TranslateModule,
    CountdownComponent
],
  templateUrl: './safe-trace-login.component.html',
  styleUrl: './safe-trace-login.component.scss',
})
export class SafeTraceLoginComponent implements OnInit, AfterViewInit {
  private fb = inject(FormBuilder);
  private _snackBar = inject(MatSnackBar);
  private router = inject(Router);
  private titleService = inject(Title);
  private translate = inject(TranslateService);
  private authService = inject(AuthService);
  private themeService = inject(ThemeService);
  private openModalConfirmService = inject(OpenModalConfirmService);

  public showLogin = true;
  public showChangePassword = false;
  public showPassword = false;
  public showForgotPassword = false;
  public showConfirmingEmail = false;
  currentImageIndex = 0;

  usernameFocused = false;
  passwordFocused = false;
  codeFocused = false;
  passwordFocusedConfirm = false;
  emailFocused = false;
  showPasswordConfirm = false;
  incorrectPassword = false;
  doubleAuthentication = false;
  timeExpired = false;
  doubleAuthenticationCount = 3;

  form: FormGroup = this.fb.group({
    username: ['', Validators.required],
    currentPassword: ['', Validators.required],
  });

  doubleAuthenticationForm: FormGroup = this.fb.group({
    code: ['', Validators.required],
  });

  registerForm: FormGroup = this.fb.group({
    name: ['', Validators.required],
    document: ['', Validators.required],
    registerNumber: ['', Validators.required],
    telephone: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
  });

  changePasswordForm: FormGroup = this.fb.group({
    username: ['', Validators.required],
    oldPassword: ['', Validators.required],
    newPassword: ['', [Validators.required, Validators.minLength(8), this.passwordStrengthValidator]],
    confirmNewPassword: ['', Validators.required]
  });

  resetPasswordForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
  });

  images = [
    '../../../../assets/images/safetrace/img-1.png',
    '../../../../assets/images/safetrace/img-2.jpg',
    '../../../../assets/images/safetrace/img-3.svg',
    '../../../../assets/images/safetrace/img-4.svg',
  ];

  setImage(index: number) {
    this.currentImageIndex = index;
  }

  startCarousel() {
    setInterval(() => {
      this.nextImage();
    }, 3000);
  }

  nextImage() {
    this.currentImageIndex = (this.currentImageIndex + 1) % this.images.length;
  }

  ngOnInit(): void {
    sessionStorage.clear();
    localStorage.clear();
    this.startCarousel();
    this.themeService.setThemeByThemeName('SAFETRACE');
  }

  ngAfterViewInit(): void {
    this.themeService.setThemeByThemeName('SAFETRACE');
  }

  handleClickRequestQuote() {
    this.showLogin = false;
  }

  handleClickCloseRegister() {
    this.showLogin = true;
    this.showForgotPassword = false;
    this.showChangePassword = false;
    this.emailFocused = false;
    this.incorrectPassword = false;
    this.resetPasswordForm.reset();
    this.changePasswordForm.reset();
    this.form.reset();
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  toggleShowPasswordConfirm() {
    this.showPasswordConfirm = !this.showPasswordConfirm;
  }

  handleForgetPassword(): void {
    this.showForgotPassword = true;
    this.showLogin = false;
    this.showConfirmingEmail = true;
    this.usernameFocused = false;
    this.passwordFocused = false;
    this.showPassword = false;
    this.incorrectPassword = false;
    this.form.reset();
  }

  redirectByToken() {
    const userItem = sessionStorage.getItem('user');
    const user = userItem ? JSON.parse(userItem) : {};
    if (user.authorities.some((item: string) => item === 'ROLE_SAFETRACE_ADMIN')) {
      this.titleService.setTitle('Safetrace');
      sessionStorage.setItem('systemType', 'SAFETRACE');
      this.router.navigate(['/panel/dashboard']);
      this._snackBar.open(
        this.translate.instant('LOGIN.SUCCESS'),
        undefined,
        {
          panelClass: ['alert_success'],
          horizontalPosition: 'right',
          verticalPosition: 'top',
        }
      );
    } else if (user.authorities.some((item: string) => item === 'ROLE_SAFE_SUPORTE')) {
      this.titleService.setTitle('Safetrace');
      sessionStorage.setItem('systemType', 'SAFETRACE');
      this.router.navigate(['/support-panel']);
      this._snackBar.open(
        this.translate.instant('LOGIN.SUCCESS'),
        undefined,
        {
          panelClass: ['alert_success'],
          horizontalPosition: 'right',
          verticalPosition: 'top',
        }
      );
    } else if (user.confirmPersonalData) {
      this.router.navigate(['/public-panel/'], {
        queryParams: { userId: user.customerId },
      });
    } else if (user.authorities.some((item: string) => item === 'ROLE_SAFETRACE_REQUESTER' || 'ROLE_SAFE_FLV_NAC')) {
      this.titleService.setTitle('Safetrace');
      sessionStorage.setItem('systemType', 'SAFETRACE');
      this.router.navigate(['/client-panel/instructions']);
    }
  }

  submitForm() {
    this.incorrectPassword = false;
    if (this.form.valid) {
      const username = this.form.get('username')?.value;
      const password = this.form.get('currentPassword')?.value;
      this.authService.login(username, password).subscribe(
        (res) => {
          this.redirectByToken();
        },
        (error) => {
          const errorType = JSON.parse(error.error)
          if (errorType?.status === "TWO_FACTOR_REQUIRED") {
            if(this.timeExpired === true) {
              this.openModalConfirmService.openModalConfirm({
                text: 'Token expirado!.',
                subText: 'Um novo token de autenticação foi enviado para o e-mail do usuário.',
                type: 'warning',
                hideCancelButton: true,
              }).subscribe(() => {
                this.doubleAuthentication = true
                this.showLogin = false
                this.timeExpired = false
                this.showChangePassword = false;
              });
              return;
            }
            this.openModalConfirmService.openModalConfirm({
              text: 'Um token de autenticação foi enviado para o e-mail do usuário.',
              subText: 'Digite o token recebido para prosseguir com o acesso ao sistema.',
              type: 'warning',
              hideCancelButton: true,
            }).subscribe(() => {
              this.doubleAuthentication = true;
              this.showLogin = false;
              this.showChangePassword = false;
            });
            return;
          }

          if (errorType?.status === 'CHANGE_PASSWORD') {
            this.incorrectPassword = true;
            this.showChangePassword = true;
            this.showLogin = false;
            this.doubleAuthentication = false;
            this.changePasswordForm.get('newPassword')?.setValue('');
            this.changePasswordForm.get('confirmNewPassword')?.setValue('');
            this._snackBar.open(
              this.translate.instant('LOGIN.CHANGE_PASSWORD'),
              undefined,
              {
                panelClass: ['alert_error'],
                horizontalPosition: 'right',
                verticalPosition: 'top',
              }
            );
          } else {
            this.incorrectPassword = true;
            this._snackBar.open(
              this.translate.instant('LOGIN.ERROR'),
              undefined,
              {
                panelClass: ['alert_error'],
                horizontalPosition: 'right',
                verticalPosition: 'top',
              }
            );
          }
        }
      );
    } else {
      this.incorrectPassword = true;
    }
  }

  submitChangePasswordForm() {
    this.changePasswordForm.markAllAsTouched();
    const oldPassword = this.form.get('currentPassword')?.value;
    const username = this.form.get('username')?.value;
    this.changePasswordForm.get('oldPassword')?.setValue(oldPassword);
    this.changePasswordForm.get('username')?.setValue(username);

    if (this.changePasswordForm.valid) {
      const payload = {
        username: this.changePasswordForm.get('username')?.value.trim(),
        oldPassword: this.changePasswordForm.get('oldPassword')?.value.trim(),
        newPassword: this.changePasswordForm.get('newPassword')?.value.trim(),
      }
      this.authService.changePassword(payload).subscribe({
        next: () => {
          this._snackBar.open(
            this.translate.instant('Senha atualizada com sucesso!'),
            undefined,
            {
              panelClass: ['alert_success'],
              horizontalPosition: 'right',
              verticalPosition: 'top',
            }
          );
          this.form.get('currentPassword')?.setValue(this.changePasswordForm.get('newPassword')?.value.trim());
          this.submitForm();
        },
        error: (error) => {
          this._snackBar.open(
            this.translate.instant('FORGOT-PASSWORD.ERROR'),
            undefined,
            {
              panelClass: ['alert_error'],
              horizontalPosition: 'right',
              verticalPosition: 'top',
            }
          );
        },
      });
    }
  }

  submitResetPasswordForm(): void {
    const frontUrl = window.location.href;

    this.resetPasswordForm.markAllAsTouched();
    if (this.resetPasswordForm.valid) {
      const email = this.resetPasswordForm.get('email')?.value.trim();
      this.authService.sendEmailResetPassword(email, frontUrl).subscribe({
        next: () => {
          this._snackBar.open(
            this.translate.instant('FORGOT-PASSWORD.SUCCESS'),
            undefined,
            {
              panelClass: ['alert_success'],
              horizontalPosition: 'right',
              verticalPosition: 'top',
            }
          );
          this.handleClickCloseRegister();
        },
        error: (error) => {
          this._snackBar.open(
            this.translate.instant('FORGOT-PASSWORD.ERROR'),
            undefined,
            {
              panelClass: ['alert_error'],
              horizontalPosition: 'right',
              verticalPosition: 'top',
            }
          );
        },
      });
    }
  }

  submitDoubleAuthentication() {
    const username = this.form.get('username')?.value;
    const code = this.doubleAuthenticationForm.get('code')?.value;
    this.doubleAuthenticationForm.markAllAsTouched();

    if (this.doubleAuthenticationForm.valid) {
      this.authService.doubleAuthentication(username, code).subscribe({
        next: () => {
          this._snackBar.open(
            this.translate.instant('FORGOT-PASSWORD.SUCCESS'),
            undefined,
            {
              panelClass: ['alert_success'],
              horizontalPosition: 'right',
              verticalPosition: 'top',
            }
          );
           this.redirectByToken();
        },
        error: (error) => {
          this.doubleAuthenticationCount = this.doubleAuthenticationCount - 1;
          this.doubleAuthenticationForm.get('code')?.setValue('');

          if (this.doubleAuthenticationCount > 0) {
            this.openModalConfirmService.openModalConfirm({
              text: 'Código inválido.',
              subText: `Você ainda possui ${this.doubleAuthenticationCount} tentativa${this.doubleAuthenticationCount > 1 ? 's' : ''} restante${this.doubleAuthenticationCount > 1 ? 's' : ''}.`,
              type: 'error',
              hideCancelButton: true,
            }).subscribe(() => {
              this.redirectByToken();
            });
          } else {
            this.openModalConfirmService.openModalConfirm({
              text: 'Número máximo de tentativas atingido.',
              subText: 'Por favor, tente novamente mais tarde ou entre em contato com a equipe de suporte para obter assistência.',
              type: 'warning',
              hideCancelButton: true,
             }).subscribe(() => {
              this.doubleAuthentication = false;
              this.showLogin = true;
              this.doubleAuthenticationCount = 3;
            });
          }
        }
      });
    }
  }

  passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
    const value: string = control.value || '';

    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumber = /\d/.test(value);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);

    const isValid =
      value.length >= 8 &&
      hasUpperCase &&
      hasLowerCase &&
      hasNumber &&
      hasSpecialChar;

    return isValid ? null : { passwordStrength: true };
  }

  onFocus(input: string) {
    if (input === 'username') {
      this.usernameFocused = true;
      this.onBlur('password');
    } else if (input === 'currentPassword') {
      this.passwordFocused = true;
    } else if (input === 'email') {
      this.emailFocused = true;
    } else if (input === 'confirmNewPassword') {
      this.passwordFocusedConfirm = true;
    } else if (input === 'code') {
      this.codeFocused = true;
    }
  }

  onBlur(input: string) {
    if (input === 'username') {
      this.usernameFocused = false;
    } else if (input === 'currentPassword') {
      this.passwordFocused = false;
    } else if (input === 'email') {
      this.emailFocused = true;
    } else if (input === 'confirmNewPassword') {
      this.passwordFocusedConfirm = true;
    } else if (input === 'code') {
      this.codeFocused = true;
    }
  }

  onCountdownFinished() {
    this.timeExpired = true;
    this.submitForm();
  }
}
